/**
 * @fileoverview IndexedDB Queue Persistence Layer
 * @module frontend/services/tracking/queue-persistence
 *
 * Provides persistent storage for tracking events using IndexedDB.
 * Survives page reloads and browser restarts.
 */

/**
 * Configuration for queue persistence
 */
export interface QueuePersistenceConfig {
  /** Database name */
  dbName: string
  /** Store name within the database */
  storeName: string
  /** Database version */
  version: number
}

/**
 * Stored queue item with metadata
 */
export interface StoredQueueItem<T> {
  id: string
  data: T
  timestamp: number
  retryCount: number
}

/**
 * IndexedDB Queue Persistence Layer
 *
 * Provides type-safe persistence for tracking events.
 * Handles database initialization, save, load, and cleanup.
 *
 * @example
 * ```typescript
 * const persistence = new QueuePersistence<TrackingEvent>({
 *   dbName: 'tracking-db',
 *   storeName: 'events',
 *   version: 1
 * })
 *
 * // Save events
 * await persistence.save(events)
 *
 * // Load events
 * const loaded = await persistence.load()
 *
 * // Clear all events
 * await persistence.clear()
 * ```
 */
export class QueuePersistence<T> {
  private db: IDBDatabase | null = null
  private config: QueuePersistenceConfig
  private initPromise: Promise<void> | null = null

  constructor(config: QueuePersistenceConfig) {
    this.config = config
  }

  /**
   * Initialize the database
   * Creates object store if it doesn't exist
   */
  private async initialize(): Promise<void> {
    if (this.db) return

    // Reuse existing init promise if already in progress
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(this.config.dbName, this.config.version)

        request.onerror = () => {
          console.error('Failed to open IndexedDB:', request.error)
          reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`))
        }

        request.onsuccess = () => {
          this.db = request.result
          // console.log(`✅ IndexedDB initialized: ${this.config.dbName}`)
          resolve()
        }

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result

          // Create object store if it doesn't exist
          if (!db.objectStoreNames.contains(this.config.storeName)) {
            const store = db.createObjectStore(this.config.storeName, { keyPath: 'id' })
            store.createIndex('timestamp', 'timestamp', { unique: false })
            store.createIndex('retryCount', 'retryCount', { unique: false })
            // console.log(`✅ Created object store: ${this.config.storeName}`)
          }
        }
      } catch (error) {
        console.error('Failed to initialize IndexedDB:', error)
        reject(error)
      }
    })

    return this.initPromise
  }

  /**
   * Save events to IndexedDB
   * @param events - Array of events to save
   * @returns Promise that resolves when all events are saved
   */
  async save(events: T[]): Promise<void> {
    await this.initialize()

    if (!this.db) {
      throw new Error('IndexedDB not initialized')
    }

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.config.storeName], 'readwrite')
        const store = transaction.objectStore(this.config.storeName)

        // Add each event with metadata
        events.forEach((event, index) => {
          const item: StoredQueueItem<T> = {
            id: `${Date.now()}-${index}-${Math.random().toString(36).substring(2, 9)}`,
            data: event,
            timestamp: Date.now(),
            retryCount: 0,
          }

          const request = store.add(item)

          request.onerror = () => {
            console.error('Failed to save event to IndexedDB:', request.error)
          }
        })

        transaction.oncomplete = () => {
          // console.log(`✅ Saved ${events.length} events to IndexedDB`)
          resolve()
        }

        transaction.onerror = () => {
          console.error('Transaction failed:', transaction.error)
          reject(new Error(`Transaction failed: ${transaction.error?.message}`))
        }
      } catch (error) {
        console.error('Failed to save events:', error)
        reject(error)
      }
    })
  }

  /**
   * Load all events from IndexedDB
   * @returns Promise that resolves to array of stored events
   */
  async load(): Promise<T[]> {
    await this.initialize()

    if (!this.db) {
      throw new Error('IndexedDB not initialized')
    }

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.config.storeName], 'readonly')
        const store = transaction.objectStore(this.config.storeName)
        const request = store.getAll()

        request.onerror = () => {
          console.error('Failed to load events from IndexedDB:', request.error)
          reject(new Error(`Failed to load events: ${request.error?.message}`))
        }

        request.onsuccess = () => {
          const items = request.result as StoredQueueItem<T>[]
          const events = items.map(item => item.data)
          // console.log(`✅ Loaded ${events.length} events from IndexedDB`)
          resolve(events)
        }
      } catch (error) {
        console.error('Failed to load events:', error)
        reject(error)
      }
    })
  }

  /**
   * Delete specific events from IndexedDB
   * @param ids - Array of event IDs to delete
   * @returns Promise that resolves when all events are deleted
   */
  async delete(ids: string[]): Promise<void> {
    await this.initialize()

    if (!this.db) {
      throw new Error('IndexedDB not initialized')
    }

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.config.storeName], 'readwrite')
        const store = transaction.objectStore(this.config.storeName)

        ids.forEach(id => {
          const request = store.delete(id)
          request.onerror = () => {
            console.error(`Failed to delete event ${id}:`, request.error)
          }
        })

        transaction.oncomplete = () => {
          // console.log(`✅ Deleted ${ids.length} events from IndexedDB`)
          resolve()
        }

        transaction.onerror = () => {
          console.error('Transaction failed:', transaction.error)
          reject(new Error(`Transaction failed: ${transaction.error?.message}`))
        }
      } catch (error) {
        console.error('Failed to delete events:', error)
        reject(error)
      }
    })
  }

  /**
   * Clear all events from IndexedDB
   * @returns Promise that resolves when all events are cleared
   */
  async clear(): Promise<void> {
    await this.initialize()

    if (!this.db) {
      throw new Error('IndexedDB not initialized')
    }

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.config.storeName], 'readwrite')
        const store = transaction.objectStore(this.config.storeName)
        const request = store.clear()

        request.onerror = () => {
          console.error('Failed to clear IndexedDB:', request.error)
          reject(new Error(`Failed to clear: ${request.error?.message}`))
        }

        transaction.oncomplete = () => {
          // console.log(`✅ Cleared all events from IndexedDB`)
          resolve()
        }

        transaction.onerror = () => {
          console.error('Transaction failed:', transaction.error)
          reject(new Error(`Transaction failed: ${transaction.error?.message}`))
        }
      } catch (error) {
        console.error('Failed to clear events:', error)
        reject(error)
      }
    })
  }

  /**
   * Get count of events in IndexedDB
   * @returns Promise that resolves to the number of events
   */
  async count(): Promise<number> {
    await this.initialize()

    if (!this.db) {
      throw new Error('IndexedDB not initialized')
    }

    return new Promise((resolve, reject) => {
      try {
        const transaction = this.db!.transaction([this.config.storeName], 'readonly')
        const store = transaction.objectStore(this.config.storeName)
        const request = store.count()

        request.onerror = () => {
          console.error('Failed to count events:', request.error)
          reject(new Error(`Failed to count: ${request.error?.message}`))
        }

        request.onsuccess = () => {
          // console.log(`✅ Event count: ${request.result}`)
          resolve(request.result)
        }
      } catch (error) {
        console.error('Failed to count events:', error)
        reject(error)
      }
    })
  }

  /**
   * Check if IndexedDB is available
   * @returns true if IndexedDB is available, false otherwise
   */
  static isAvailable(): boolean {
    try {
      return !!window.indexedDB
    } catch {
      return false
    }
  }

  /**
   * Close the database connection
   */
  close(): void {
    if (this.db) {
      this.db.close()
      this.db = null
      // console.log(`✅ Closed IndexedDB: ${this.config.dbName}`)
    }
  }
}
